// ═══════════════════════════════════════════════════════════════
// Stock Monitor — Utility Helpers v1.9
// Aggiornato: calcRisk → 7 profili con controllo coerenza
// ═══════════════════════════════════════════════════════════════

/**
 * Restituisce il simbolo valuta in base al ticker
 */
export function getCurrency(symbol) {
  if (!symbol) return "$";
  if (symbol.endsWith(".MI")) return "€";
  return "$";
}

/**
 * Converte il formato output dell'analyst in Markdown valido.
 * Gestisce separatori ═══, emoji headings, e bullet points.
 */
export function formatAnalystText(text) {
  if (!text) return "";
  let out = text;
  // Rimuovi linee di separazione ═══
  out = out.replace(/[═]{3,}/g, "---");
  // Converti emoji + TESTO MAIUSCOLO in heading markdown
  out = out.replace(/^(#{1,3}\s)/gm, "$1"); // preserva heading esistenti
  out = out.replace(/^([📊📈📰✅⚠️🎯🟢🟡🔴🇮🇹💡🏦])\s*([A-ZÀÈÉÌÒÙ][A-ZÀÈÉÌÒÙ\s/\-—&]{2,})/gm, "### $1 $2");
  // Pulisci doppi ---
  out = out.replace(/---\s*---/g, "---");
  return out;
}

// Backward compatibility alias
export const formatAdvisorText = formatAnalystText;

/**
 * Calcola il profilo di rischio basato su 12 risposte.
 * Sistema a punteggio pesato con controllo coerenza.
 * 
 * Dimensioni:
 * - Capacità oggettiva (50% peso): età, reddito, risparmio, capitale, orizzonte, obiettivo
 * - Disponibilità soggettiva (40% peso): esperienza, conoscenza, scenari, tolleranza perdita
 * - Sostenibilità (10%): ESG → non impatta il profilo rischio, è un overlay
 * 
 * Output: una delle 7 chiavi di RISK_PROFILES
 */
export function calcRisk(answers) {
  let objScore = 0;   // Capacità oggettiva (0-21 punti)
  let subjScore = 0;  // Disponibilità soggettiva (0-16 punti)
  let coherenceFlag = false; // Se true, shift verso conservativo

  // ═══ CAPACITÀ OGGETTIVA (max 21 punti) ═══

  // Età — giovani possono rischiare di più
  const ageMap = { "18-30": 4, "31-40": 3, "41-50": 2, "51-60": 1, "60+": 0 };
  objScore += ageMap[answers.age] ?? 2;

  // Reddito — maggiore reddito = maggiore capacità assorbire perdite
  const incomeMap = {
    "< €25.000": 0,
    "€25.000 - €50.000": 1,
    "€50.000 - €100.000": 2,
    "> €100.000": 3
  };
  objScore += incomeMap[answers.income] ?? 1;

  // Risparmio mensile
  const savingsMap = {
    "< €200": 0,
    "€200 - €500": 1,
    "€500 - €1.000": 2,
    "> €1.000": 3
  };
  objScore += savingsMap[answers.savings] ?? 1;

  // Capitale investibile
  const capitalMap = {
    "< €5.000": 0,
    "€5.000 - €20.000": 1,
    "€20.000 - €50.000": 2,
    "€50.000 - €100.000": 3,
    "> €100.000": 4
  };
  objScore += capitalMap[answers.capital] ?? 1;

  // Orizzonte temporale — più lungo = più rischio tollerabile
  const horizonMap = {
    "< 1 anno": 0,
    "1-3 anni": 1,
    "3-5 anni": 2,
    "5-10 anni": 3,
    "10+ anni": 4
  };
  objScore += horizonMap[answers.horizon] ?? 1;

  // Obiettivo
  const goalMap = {
    "Fondo emergenza / liquidità": 0,
    "Acquisto casa": 1,
    "Pensione integrativa": 2,
    "Educazione figli": 2,
    "Reddito passivo (dividendi)": 2,
    "Crescita del capitale": 3
  };
  objScore += goalMap[answers.goal] ?? 1;

  // ═══ DISPONIBILITÀ SOGGETTIVA (max 16 punti) ═══

  // Esperienza
  const expMap = {
    "Mai investito — sono alle prime armi": 0,
    "Qualche esperienza — ho investito in fondi o ETF": 2,
    "Esperto — investo attivamente in azioni, obbligazioni, derivati": 4
  };
  subjScore += expMap[answers.experience] ?? 1;

  // Conoscenza rischio
  const knowMap = {
    "Sì, lo capisco e lo accetto": 2,
    "Lo capisco in teoria, ma mi preoccupa": 1,
    "Non ne sono sicuro/a": 0
  };
  subjScore += knowMap[answers.knowledgeCheck] ?? 0;

  // Scenario -20%
  const dropMap = {
    "Compro di più — è un'opportunità": 4,
    "Mantengo e aspetto la ripresa": 2,
    "Vendo parte per limitare le perdite": 1,
    "Vendo tutto — non posso sopportarlo": 0
  };
  subjScore += dropMap[answers.scenarioDrop] ?? 1;

  // Scenario gamble
  const gambleMap = {
    "€1.000 garantiti subito": 0,
    "50% di probabilità di ricevere €2.500 (o niente)": 2
  };
  subjScore += gambleMap[answers.scenarioGamble] ?? 0;

  // Perdita massima tollerata
  const lossMap = {
    "Max 5%": 0,
    "Max 10%": 1,
    "Max 20%": 2,
    "Max 30%": 3,
    "Oltre 30% — se il potenziale è alto": 4
  };
  subjScore += lossMap[answers.maxLoss] ?? 1;

  // ═══ CONTROLLO COERENZA ═══
  const isExperienced = answers.experience?.includes("Esperto");
  const wouldSellAll = answers.scenarioDrop?.includes("Vendo tutto");
  const highLossOk = answers.maxLoss?.includes("30") || answers.maxLoss?.includes("Oltre");
  const choosesGuaranteed = answers.scenarioGamble?.includes("garantiti");
  const lowDrop = answers.scenarioDrop?.includes("Vendo tutto") || answers.scenarioDrop?.includes("Vendo parte");

  if (isExperienced && wouldSellAll) coherenceFlag = true;
  if (highLossOk && wouldSellAll) coherenceFlag = true;
  if (highLossOk && choosesGuaranteed && lowDrop) coherenceFlag = true;

  // ═══ CALCOLO PROFILO FINALE ═══
  const objNorm = (objScore / 21) * 50;
  const subjNorm = (subjScore / 16) * 50;
  let totalScore = objNorm + subjNorm;

  // Se incoerente: penalità -15 punti
  if (coherenceFlag) {
    totalScore = Math.max(0, totalScore - 15);
  }

  // Mappatura su 7 profili
  if (totalScore >= 85) return "veryAggressive";
  if (totalScore >= 72) return "aggressive";
  if (totalScore >= 60) return "moderatelyAggressive";
  if (totalScore >= 47) return "moderate";
  if (totalScore >= 35) return "moderatelyConservative";
  if (totalScore >= 20) return "conservative";
  return "veryConservative";
}
